import {
  ddosRefusalOf,
  throwIfRenderRefused
} from '@shared/all/domains/ddosMode';
import { type Result } from '@shared/all/domains/request';
import { RATE_LIMITED_MESSAGE } from '@shared/all/domains/trpc';
import { resolveDevApiUrl } from '@shared/all/helpers/devApiUrl';
import { REFRESH_INTERVAL_MS } from '@shared/all/helpers/onlineStatus';
import log from '@shared/all/log';
import { noteDdosRefusal } from '@shared/utils/helpers/ddosStatus';
import { getAcccessToken as getAcccessTokenHelper } from '@shared/utils/helpers/parseJwt';
import { wrapToResult } from '@shared/utils/helpers/resultWrapper';
import toast from '@shared/utils/helpers/toast';
import { type ResultWrappedClient } from '@shared/utils/helpers/trpcClientTypes';
import {
  createTRPCClient,
  httpBatchLink,
  httpLink,
  splitLink,
  type TRPCClient,
  TRPCClientError,
  type TRPCLink
} from '@trpc/client';
import type { inferRouterInputs, inferRouterOutputs } from '@trpc/server';
import { observable } from '@trpc/server/observable';
import SuperJSON from 'superjson';

import type { AppRouter } from '../../../server/src/router';
import eventEmitter from '../helpers/eventEmitter';
import { webServerHeaders } from './webServerSecret';

// In dev, force the API URL hostname to match the browser's hostname so XHRs
// stay same-origin. Otherwise mixing localhost ↔ LAN IP triggers cross-site
// SameSite cookie rejection (Firefox especially) and fails auth. See
// resolveDevApiUrl for why that only applies to loopback/LAN-IP API hosts.
function resolveApiBaseUrl(): string {
  const envUrl = (process.env.NEXT_PUBLIC_API_URL || '').replace(/\/+$/, '');
  if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
    return resolveDevApiUrl(envUrl, window.location.hostname).replace(
      /\/+$/,
      ''
    );
  }
  return envUrl;
}

const apiBaseUrl = resolveApiBaseUrl();
const url = `${apiBaseUrl}/trpc`;
const proxyUrl = '/api/trpc'; // Next.js API route proxy

log('success', `NEXT_PUBLIC_API_URL: ${apiBaseUrl}`);
log('success', `tRPC URL: ${url}`);
log('success', `Proxy URL: ${proxyUrl}`);

/**
 * Creates a fetch function with credentials. `onResponse` runs after every
 * successful response so callers can hook into cookie rotation without
 * touching the link layer.
 */
const createFetchWithCredentials = (
  credentials: RequestCredentials = 'include',
  onResponse?: () => void
) => {
  return async (url: RequestInfo | URL, options?: RequestInit) => {
    const response = await fetch(url, {
      ...(options as RequestInit),
      credentials
    });
    if (response.status === 429) {
      throw new TRPCClientError(RATE_LIMITED_MESSAGE);
    }
    onResponse?.();
    return response;
  };
};

/**
 * Recursively wraps tRPC client at any depth.
 * Intercepts query/mutate calls and wraps them with unwrapFromResult.
 */
const createRecursiveProxy = (
  target: unknown,
  wrap: <T>(promise: Promise<T>) => Promise<Result<T>> = wrapToResult
): unknown => {
  return new Proxy((() => {}) as unknown as object, {
    get(_, prop) {
      if (typeof prop === 'symbol') {
        return Reflect.get(target as object, prop);
      }

      const value = Reflect.get(target as object, prop);

      // If accessing query or mutate, wrap the function
      if (prop === 'query' || prop === 'mutate') {
        if (typeof value === 'function') {
          return (...args: unknown[]) => {
            // Pass through all arguments including context options
            return wrap(value(...args) as Promise<unknown>);
          };
        }
      }

      // Recursively wrap any value that could be a router/procedure
      if (value !== null && value !== undefined) {
        return createRecursiveProxy(value, wrap);
      }

      return value;
    },
    // Handle function calls (for callable proxies)
    apply(_, __, args) {
      if (typeof target === 'function') {
        return (target as (...a: unknown[]) => unknown)(...args);
      }
      return undefined;
    }
  });
};

class ClientService {
  client: ResultWrappedClient<TRPCClient<AppRouter>>;
  customLink: TRPCLink<AppRouter>;
  self: ClientService;
  refreshingPromise: Promise<void> | null = null;
  lastRefreshedAt = 0;
  isOnline = true;
  lastClientToken: string =
    typeof document === 'undefined' ? '' : getAcccessTokenHelper();

  checkCookieRotation = () => {
    if (typeof document === 'undefined') return;
    const current = getAcccessTokenHelper();
    if (current !== this.lastClientToken) {
      this.lastClientToken = current;
      eventEmitter.emit('userCookieRotated');
    }
  };

  constructor() {
    this.self = this;
    this.customLink = () => {
      return ({ next, op }) => {
        return observable((observer) => {
          const executeRequest = async () => {
            if (!op.path.startsWith('auth.')) {
              await this.refreshIfNeeded();
            }

            const unsubscribe = next(op).subscribe({
              next: (value) => {
                // Always emit setOnline on successful queries - idempotent and reliable
                if (!this.isOnline) {
                  this.isOnline = true;
                }
                eventEmitter.emit('setOnline');
                observer.next(value);
              },
              error: (err) => {
                // Handle rate limiting (429). Checked before the network
                // branch: the fetch wrapper throws it with no `data`, so it
                // would otherwise be read as "unreachable".
                if (
                  err.message === RATE_LIMITED_MESSAGE ||
                  err.data?.httpStatus === 429
                ) {
                  if (!op.context?.silenceErrors) {
                    toast('Too many requests - please slow down', '', 'error');
                  }
                  observer.error(err);
                  return;
                }
                //if network error then emit setOffline
                if (err.name === 'TRPCClientError' && !err.data) {
                  if (this.isOnline) {
                    eventEmitter.emit('setOffline');
                    this.isOnline = false;
                  }
                  // Don't show toast for network errors - the maintenance
                  // splash says it. The raw messages ("Failed to fetch", or a
                  // JSON parse error on the proxy's HTML 503 page) tell the
                  // user nothing.
                  observer.error(err);
                  return;
                }
                // DDoS mode (.specs/ddos-mode.md). The server is saying who
                // may use it right now, not that this call went wrong, so no
                // toast. Recording it is what moves the page: DdosGate reads
                // the same store and sends a logged-out visitor to /login and
                // an unverified one to /biometric-verification, which is how a
                // flag flipped mid-session takes effect without a reload.
                const ddosRefusal = ddosRefusalOf(err);
                if (ddosRefusal) {
                  noteDdosRefusal(ddosRefusal);
                  observer.error(err);
                  return;
                }
                if (err.data?.code === 'UNAUTHORIZED') {
                  // UNAUTHORIZED means session is revoked/banned — log out
                  if (err.message !== 'ACTIVE_SESSION_SWITCHED')
                    eventEmitter.emit('logout');
                  observer.error(err);
                } else if (
                  // @factiii/auth's own re-verify timeout, which only fires if
                  // an admin sets `biometricVerificationTimeoutMs`. The package
                  // gives it no code, only this sentence.
                  err.data?.code === 'FORBIDDEN' &&
                  err.message?.includes('Biometric verification') &&
                  window.location.pathname !== '/biometric-verification'
                ) {
                  window.location.href = '/biometric-verification';
                  observer.error(err);
                } else {
                  const validationErrors = err.data?.zodError?.fieldErrors;
                  let message: string | undefined;
                  if (validationErrors) {
                    const errors = Object.values(validationErrors).flat();
                    message = String(errors[0]);
                  } else message = err.message;
                  if (
                    !op.context?.silenceErrors &&
                    message &&
                    !message.includes('Biometric verification')
                  )
                    toast(message, '', 'error');
                  // Always propagate the error to complete the observable
                  observer.error(err);
                }
              },
              complete() {
                observer.complete();
              }
            });
            return unsubscribe;
          };
          void executeRequest();
        });
      };
    };

    const rawClient = createTRPCClient<AppRouter>({
      links: [
        this.customLink,
        splitLink({
          // auth.* must use the proxy URL (handles cookie rotation/refresh) and
          // stay on a plain httpLink — it can't be folded into a batch.
          condition: (op) => op.path.startsWith('auth.'),
          true: httpLink({
            url: proxyUrl,
            transformer: SuperJSON,
            fetch: createFetchWithCredentials(
              'include',
              this.checkCookieRotation
            )
          }),
          // Everything else batches. Initial page load fans out ~20 queries; the
          // server rate-limits at 20 req/sec/token, so unbatched calls trip the
          // "Too many requests" toast. Batching collapses the burst into one or
          // two HTTP requests. maxURLLength splits a batch whose GET URL would
          // pass the limit. There is no fallback to POST: a query too long on
          // its own is refused with "Input is too big for a single dispatch",
          // so keep list inputs short (fileHash.ts → HASHES_PER_CHECK).
          false: httpBatchLink({
            url: url,
            transformer: SuperJSON,
            maxURLLength: 8000,
            fetch: createFetchWithCredentials(
              'include',
              this.checkCookieRotation
            )
          })
        })
      ]
    });

    this.client = createRecursiveProxy(rawClient) as ResultWrappedClient<
      TRPCClient<AppRouter>
    >;
  }
  async refreshIfNeeded(): Promise<void> {
    if (!(await this.getAcccessToken())) return;
    if (Date.now() - this.lastRefreshedAt < REFRESH_INTERVAL_MS) return;
    if (this.refreshingPromise) {
      await this.refreshingPromise;
      return;
    }
    this.refreshingPromise = this.client.auth.refresh
      .query()
      .then(() => {
        this.lastRefreshedAt = Date.now();
        this.refreshingPromise = null;
      })
      .catch(() => {
        this.refreshingPromise = null;
      });
    await this.refreshingPromise;
  }
  async getAcccessToken() {
    return getAcccessTokenHelper();
  }
  clearTokens() {
    this.lastRefreshedAt = 0;
    this.refreshingPromise = null;
  }
}

export const service = new ClientService();

export const client = service.client;

export type RouterOutputs = inferRouterOutputs<AppRouter>;
export type RouterInputs = inferRouterInputs<AppRouter>;

// A server render the API refused (DDoS mode on, no valid WEB_SERVER_SECRET)
// is thrown, never returned as the tuple's error. Every caller of `ssrClient`
// treats an error as "this does not exist" or falls back to an empty state,
// and rendered that way a refusal reaches crawlers as a not-found page. The
// throw carries a digest the pages and the Open Graph routes recognise
// (utils/renderRefusal.ts); see .specs/ddos-mode.md.
const wrapServerRender = async <T>(promise: Promise<T>): Promise<Result<T>> => {
  const result = await wrapToResult(promise);
  throwIfRenderRefused(result[0]);
  return result;
};

export const ssrClient = createRecursiveProxy(
  createTRPCClient<AppRouter>({
    links: [
      httpLink({
        url: url,
        transformer: SuperJSON,
        // The server render's own credential, so DDoS mode does not refuse it
        // as a logged-out visitor. See utils/webServerSecret.ts.
        headers: webServerHeaders,
        fetch: createFetchWithCredentials('omit') // Don't use browser cookies in SSR
      })
    ]
  }),
  wrapServerRender
) as ResultWrappedClient<TRPCClient<AppRouter>>;

/**
 * SSR tRPC client that forwards the given cookie header (e.g. from Next.js cookies()).
 * Use for server components that need authenticated tRPC calls.
 */
export function getSSRClient(
  cookieHeader: string
): ResultWrappedClient<TRPCClient<AppRouter>> {
  return createRecursiveProxy(
    createTRPCClient<AppRouter>({
      links: [
        httpLink({
          url,
          transformer: SuperJSON,
          fetch: (input, init) =>
            fetch(input, {
              ...init,
              headers: {
                ...(init?.headers as Record<string, string>),
                Cookie: cookieHeader
              },
              cache: 'no-store'
            })
        })
      ]
    })
  ) as ResultWrappedClient<TRPCClient<AppRouter>>;
}

export type TRPCClientErrorType = TRPCClientError<AppRouter>;
